import { type EntryType, type FeedEvent, type FeedResponse, type PaginatedEventsResult } from '@/lib/api/apiTypes';
import { useAuth } from '@/lib/auth/auth';
import { mapDiscoveryEventToFeedEvent } from '@/features/events/feed/eventDiscoveryMappers';
import { buildFeedPath } from '@/features/events/feed/feedQuery';
import type { FeedDistanceFilter, FeedTimeWindow } from '@/features/events/feed/feedPreferences';
import { useUserOriginCoords } from '@/lib/location/locationOrigin';
import { getDeviceTimeZone } from '@/lib/utils';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

const CITYWIDE_DISCOVER_RADIUS_METERS: FeedDistanceFilter = 80000;

export const citywideEventKeys = {
  all: ['citywide-events'] as const,
  list: (
    timeWindow: FeedTimeWindow,
    viewerKey: string,
    originLatitude: number,
    originLongitude: number,
    entryType?: EntryType | null,
    attributes: string[] = []
  ) =>
    [
      ...citywideEventKeys.all,
      timeWindow,
      viewerKey,
      originLatitude,
      originLongitude,
      entryType ?? '',
      attributes.join(','),
    ] as const,
};

export function useCitywideEvents(
  timeWindow: FeedTimeWindow,
  entryType: EntryType | null = null,
  attributes: string[] = [],
  enabled = true
) {
  const session = useAuth((state) => state.session);
  const isHydrating = useAuth((state) => state.isHydrating);
  const request = useAuth((state) => state.request);
  const origin = useUserOriginCoords();
  const viewerKey = session?.me.user.id ?? 'anon';

  return useQuery({
    queryKey: citywideEventKeys.list(
      timeWindow,
      viewerKey,
      origin.latitude,
      origin.longitude,
      entryType,
      attributes
    ),
    enabled: enabled && !isHydrating,
    queryFn: async (): Promise<FeedEvent[]> => {
      if (session) {
        const result = await request<FeedResponse>(
          buildFeedPath({
            latitude: origin.source === 'gps' ? origin.latitude : null,
            longitude: origin.source === 'gps' ? origin.longitude : null,
            originLatitude: origin.latitude,
            originLongitude: origin.longitude,
            radiusMeters: CITYWIDE_DISCOVER_RADIUS_METERS,
            timeWindow,
            limit: 40,
            offset: 0,
            mode: 'smart',
            entryType,
            attributes,
          })
        );
        return result.events;
      }

      const params = new URLSearchParams({
        limit: '40',
        offset: '0',
        sort: 'soonest',
        lat: String(origin.latitude),
        lng: String(origin.longitude),
        origin_lat: String(origin.latitude),
        origin_lng: String(origin.longitude),
        time_window: timeWindow,
        tz: getDeviceTimeZone(),
      });
      if (entryType) {
        params.set('entry_type', entryType);
      }
      if (attributes.length > 0) {
        params.set('attributes', attributes.join(','));
      }
      const result = await request<PaginatedEventsResult>(`/events?${params.toString()}`);
      return result.events.map(mapDiscoveryEventToFeedEvent);
    },
    placeholderData: keepPreviousData,
  });
}
